'use client';

import { NumberedPagination } from '~/components/primitives';
import type { Deal, User } from '@funkey/types/graphql/graphql';
import { useI18n } from '~/features/i18n';
import Table from '~/components/primitives/Table';
import { usePortalCtx } from '~/features/global/portal';
import { Auth } from '~/modules/auth/client';
import { DateTime } from '~/components/primitives/DateTime';

export const BizzEventsTable: React.FC<{
    deals: (Pick<
        Deal,
        | 'id'
        | 'name'
        | 'status'
        | 'itemCount'
        | 'startsAt'
        | 'endsAt'
        | 'participants'
        | 'address'
        | 'firstName'
        | 'lastName'
    > & { user?: Pick<User, 'id' | 'firstName' | 'lastName' | 'email'> | null })[];
    pageSize?: number;
}> = props => {
    const i18n = useI18n();
    const auth = Auth.useSession('required');
    const portal = usePortalCtx('BIZZ');
    const deals = props.deals.filter(d => {
        if (portal.permissions.includes('BIZZ_VIEW_ACTIVITIES_OTHERS')) {
            return true;
        }

        return d?.user?.id === auth.user.id;
    });
    const pagination = NumberedPagination.useState(deals.length, props.pageSize);

    return (
        <>
            <Table
                data-testid="bizz-events-table"
                head={
                    <>
                        {[
                            i18n._('Name'),
                            i18n._('Person responsible'),
                            i18n._('Activities'),
                            i18n._('Date'),
                            i18n._('Time'),
                            i18n._('Participants'),
                            i18n._('Location'),
                        ].map(label => (
                            <Table.HeaderCell key={label}>{label}</Table.HeaderCell>
                        ))}
                    </>
                }
            >
                <Table.Body>
                    {deals
                        .slice(pagination.pageBeginIndex, pagination.pageEndIndex)
                        .map(deal => ({
                            ...deal,
                            startsAt: new Date(deal.startsAt),
                            endsAt: deal.endsAt ? new Date(deal.endsAt) : null,
                        }))
                        .map(x => {
                            const deal = {
                                ...x,
                                startsAt: !!x ? new Date(x.startsAt) : null,
                                endsAt: !!x ? (x.endsAt ? new Date(x.endsAt) : null) : null,
                            };

                            return (
                                <Table.Row
                                    key={deal.id}
                                    href={`/${i18n.locale}/bizz/${portal.bizz.slug}/events/${deal.id}`}
                                >
                                    <Table.Cell>{deal.name}</Table.Cell>
                                    <Table.Cell>
                                        {deal?.user
                                            ? `${deal.user.firstName} ${deal.user.lastName}`
                                            : `${deal.firstName} ${deal.lastName}`}
                                    </Table.Cell>
                                    <Table.Cell>{deal.itemCount}</Table.Cell>
                                    <Table.Cell>
                                        {deal.startsAt && (
                                            <DateTime
                                                format="date"
                                                // prettier-ignore
                                                date={!deal.endsAt ? deal.startsAt : { from: deal.startsAt, to: deal.endsAt }}
                                            />
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        {deal.startsAt && (
                                            <DateTime format="time" date={deal.startsAt} />
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>{deal.participants}</Table.Cell>
                                    <Table.Cell>{deal.address}</Table.Cell>
                                </Table.Row>
                            );
                        })}
                </Table.Body>
            </Table>
            <NumberedPagination
                className="mt-4"
                {...pagination}
                previousText={i18n._('Previous')}
                nextText={i18n._('Next')}
            />
        </>
    );
};
